import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import {
  IGroupedPromotion,
  IMergedPromotions,
  IPromotion,
  PromotionType,
  Unit,
  getDiscountStr,
  getNPayBenefits,
  groupByPaymentMethod,
} from 'common/promotion';
import { isPromotionTarget } from 'list/international/promotion';
import { IFareDetail, IScheduleDetail } from 'list/international/types';
import { IPassengerCount } from './types';
import { getTotalPrice } from './utils';

dayjs.extend(isSameOrAfter);

const getNPayPromotions = (
  promotions: IPromotion[],
  schedules: IScheduleDetail[][],
  fare: IFareDetail,
  { adultCnt, childCnt, infantCnt }: IPassengerCount,
) => {
  const [, eventFareType] = fare.FareType.split('/');
  return promotions.reduce<IMergedPromotions>(
    (acc, promotion) => {
      if (
        !isPromotionTarget(promotion, schedules, fare.AgtCode, fare.FareType)
      ) {
        return acc;
      }
      const passengerCount = {
        adult: promotion.passengers.adult ? adultCnt : 0,
        child: promotion.passengers.child ? childCnt : 0,
        infant: promotion.passengers.infant ? infantCnt : 0,
      };
      const totalDiscount = getNPayBenefits(
        promotion.benefit,
        getTotalPrice(
          fare,
          passengerCount.adult,
          passengerCount.child,
          passengerCount.infant,
          true,
        ),
        passengerCount,
      );
      const { paymentMethods, benefit, description } = promotion;
      if (!totalDiscount) return acc;
      if (description) acc.descriptions.push({ description, totalDiscount });
      groupByPaymentMethod(
        acc.promotionMap,
        paymentMethods?.find(({ code }) => code === eventFareType),
        PromotionType.NAVER_PAY,
        benefit,
        totalDiscount,
      );
      return acc;
    },
    { descriptions: [], promotionMap: new Map() },
  );
};

export default function getPromotions(
  activePromotions: IPromotion[],
  schedules: IScheduleDetail[][],
  fare: IFareDetail,
  passengerCount: IPassengerCount,
  originalPrice: number,
) {
  const { descriptions, promotionMap } = getNPayPromotions(
    activePromotions,
    schedules,
    fare,
    passengerCount,
  );

  const defaultBenefit = promotionMap
    .get(undefined)?.[1]
    .get(PromotionType.NAVER_PAY);

  if (defaultBenefit) {
    promotionMap.forEach(([, promotions], paymentMethod) => {
      if (!paymentMethod) return;
      const original = promotions.get(PromotionType.NAVER_PAY);
      if (!original) {
        promotions.set(PromotionType.NAVER_PAY, defaultBenefit);
        return;
      }
      original.totalDiscount += defaultBenefit.totalDiscount;
      original[Unit.PERCENT] += defaultBenefit[Unit.PERCENT] || 0;
      original[Unit.KRW] += defaultBenefit[Unit.KRW] || 0;
    });
  }

  return {
    promotions: Array.from(promotionMap)
      .filter(
        ([paymentMethod]) =>
          (promotionMap.size === 1 && promotionMap.get(undefined)) ||
          paymentMethod,
      )
      .map(([, [paymentMethod, promotions]]) =>
        Array.from(promotions).reduce<IGroupedPromotion>(
          (acc, discountInfo) => {
            const [type, promotion] = discountInfo;
            const summary = getDiscountStr(discountInfo);
            acc.summaryByType.push({ type, summary });
            acc.discountedPrice -= promotion.totalDiscount;
            return acc;
          },
          {
            otaId: fare.AgtCode,
            paymentMethod,
            summaryByType: [],
            discountedPrice: originalPrice,
          },
        ),
      )
      .sort((a, b) => a.discountedPrice - b.discountedPrice),
    descriptions: descriptions
      .sort((a, b) => b.totalDiscount - a.totalDiscount)
      .map(({ description }) => description),
  };
}
